import { IGeracaoDeMarcacoesPadraoColaboradorAfetado } from '../../../models/geracao-de-marcacoes-padrao.service.model';

export class ColaboradoresResultadoTableController {
    private static nextInstanceId = 0;

    public items: IGeracaoDeMarcacoesPadraoColaboradorAfetado[] = [];
    public showSelection = false;
    public selectedItems: IGeracaoDeMarcacoesPadraoColaboradorAfetado[] = [];
    public onSelectionChange?: ({ selectedItems }: { selectedItems: IGeracaoDeMarcacoesPadraoColaboradorAfetado[] }) => void;

    public filteredItems: IGeracaoDeMarcacoesPadraoColaboradorAfetado[] = [];
    public searchTerm = '';
    public instanceId = `colaboradores_resultado_table_${ColaboradoresResultadoTableController.nextInstanceId++}`;

    public selectedMap: { [trabalhador: string]: boolean } = {};

    $onInit() {
        this.selectedItems = this.selectedItems || [];
        this.applySearchFilter();
        this.syncSelectedMap();
    }

    $onChanges() {
        this.selectedItems = this.selectedItems || [];
        this.applySearchFilter();
        this.syncSelectedMap();
    }

    hasItems() {
        return !!this.items?.length;
    }

    isSelectable(item: IGeracaoDeMarcacoesPadraoColaboradorAfetado) {
        return !item.revertido;
    }

    search() {
        this.applySearchFilter();
    }

    hasFilteredItems() {
        return !!this.filteredItems.length;
    }

    hasSearchTerm() {
        return !!this.normalizeSearchValue(this.searchTerm);
    }

    hasItensSelecionaveis() {
        return this.getItensSelecionaveis().length > 0;
    }

    temTodosSelecionados() {
        const itensSelecionaveis = this.getItensSelecionaveis();

        return !!itensSelecionaveis.length && itensSelecionaveis.every((item) => !!this.selectedMap[item.trabalhador]);
    }

    temSelecaoParcial() {
        const itensSelecionaveis = this.getItensSelecionaveis();

        return itensSelecionaveis.some((item) => !!this.selectedMap[item.trabalhador]) && !this.temTodosSelecionados();
    }

    toggleSelection(item: IGeracaoDeMarcacoesPadraoColaboradorAfetado) {
        if (!this.isSelectable(item)) {
            return;
        }

        const isSelected = !!this.selectedMap[item.trabalhador];

        if (isSelected) {
            this.selectedItems = [
                ...this.selectedItems.filter((selectedItem) => selectedItem.trabalhador !== item.trabalhador),
                item,
            ];
        } else {
            this.selectedItems = [
                ...this.selectedItems.filter((selectedItem) => selectedItem.trabalhador !== item.trabalhador)
            ];
        }

        this.emitSelectionChange();
    }

    toggleTodos() {
        const itensSelecionaveis = this.getItensSelecionaveis();
        const trabalhadoresFiltrados = new Set((this.filteredItems || []).map((item) => item.trabalhador));
        const itensMantidos = this.selectedItems.filter((item) => !trabalhadoresFiltrados.has(item.trabalhador));

        if (!itensSelecionaveis.length) {
            return;
        }

        if (this.temTodosSelecionados()) {
            this.selectedItems = itensMantidos;
        } else {
            this.selectedItems = this.mergeSelectedItems(itensMantidos, itensSelecionaveis);
        }

        this.emitSelectionChange();
    }

    getStatusLabel(item: IGeracaoDeMarcacoesPadraoColaboradorAfetado) {
        return item.revertido ? 'Revertido' : 'Gerado';
    }

    getStatusClass(item: IGeracaoDeMarcacoesPadraoColaboradorAfetado) {
        return item.revertido ? 'label-default' : 'label-success';
    }

    private applySearchFilter() {
        const normalizedTerm = this.normalizeSearchValue(this.searchTerm);
        const items = this.items || [];

        if (!normalizedTerm) {
            this.filteredItems = items.slice();
            return;
        }

        this.filteredItems = items.filter((item) => this.matchesSearch(item, normalizedTerm));
    }

    private syncSelectedMap() {
        const selectedIds = new Set(this.selectedItems.map((item) => item.trabalhador));
        this.selectedMap = (this.items || []).reduce((acc, item) => {
            acc[item.trabalhador] = selectedIds.has(item.trabalhador);
            return acc;
        }, {} as { [trabalhador: string]: boolean });
    }

    private getItensSelecionaveis() {
        return (this.filteredItems || []).filter((item) => this.isSelectable(item));
    }

    private mergeSelectedItems(...groups: IGeracaoDeMarcacoesPadraoColaboradorAfetado[][]) {
        const selectedItemsMap = new Map<string, IGeracaoDeMarcacoesPadraoColaboradorAfetado>();

        groups.forEach((group) => {
            group.forEach((item) => {
                selectedItemsMap.set(item.trabalhador, item);
            });
        });

        return Array.from(selectedItemsMap.values());
    }

    private matchesSearch(item: IGeracaoDeMarcacoesPadraoColaboradorAfetado, normalizedTerm: string) {
        const nome = this.normalizeSearchValue(item.nome);
        const trabalhador = this.normalizeSearchValue(item.trabalhador);

        return nome.includes(normalizedTerm) || trabalhador.includes(normalizedTerm);
    }

    private normalizeSearchValue(value: string | null | undefined) {
        return (value || '').trim().toLowerCase();
    }

    private emitSelectionChange() {
        this.syncSelectedMap();

        if (this.onSelectionChange) {
            this.onSelectionChange({ selectedItems: this.selectedItems });
        }
    }
}
